import { useMemo, useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type ComplianceRow,
  type DocumentKind,
  type PractitionerDocument,
  downloadPractitionerDocument,
  listComplianceSummary,
  listPractitionerDocuments,
} from "@/api/documents";
import { runExpiringDocumentsJob } from "@/api/jobs";
import { usePermission } from "@/auth/usePermission";
import { PageHeader } from "@/components/PageHeader";

const TRACKED_KINDS: DocumentKind[] = [
  "DIPLOMA",
  "RPPS_ATTEST",
  "DPC_CERT",
  "LIABILITY_INSURANCE",
];

const KIND_SHORT: Record<DocumentKind, string> = {
  DIPLOMA: "DIP",
  RPPS_ATTEST: "RPPS",
  DPC_CERT: "DPC",
  LIABILITY_INSURANCE: "RC",
  MEDICAL_DEGREE: "MD",
  ID_CARD: "ID",
  OTHER: "—",
};

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function CompliancePage() {
  const { t } = useTranslation();
  const canRead = usePermission("document:read_all");

  const [horizonDays, setHorizonDays] = useState(90);
  const [filter, setFilter] = useState<"all" | "expired" | "missing">("all");
  const [search, setSearch] = useState("");
  const [drilldown, setDrilldown] = useState<ComplianceRow | null>(null);

  const summary = useQuery({
    queryKey: ["compliance-summary", horizonDays],
    queryFn: () => listComplianceSummary(horizonDays),
    enabled: canRead,
  });

  const drilldownDocs = useQuery({
    queryKey: ["compliance-drilldown", drilldown?.practitioner_id],
    queryFn: () =>
      drilldown ? listPractitionerDocuments(drilldown.practitioner_id) : Promise.resolve([]),
    enabled: drilldown !== null,
  });

  const expiryJob = useMutation({
    mutationFn: () => runExpiringDocumentsJob(horizonDays),
  });

  const rows = useMemo(() => {
    const all = summary.data ?? [];
    return all.filter((r) => {
      if (search && !r.last_name.toLowerCase().includes(search.toLowerCase()))
        return false;
      if (filter === "expired") return r.expired_count > 0;
      if (filter === "missing")
        return TRACKED_KINDS.some((k) => (r.counts_by_kind[k] ?? 0) === 0);
      return true;
    });
  }, [summary.data, filter, search]);

  const stats = useMemo(() => {
    const all = summary.data ?? [];
    return {
      practitioners: all.length,
      withExpired: all.filter((r) => r.expired_count > 0).length,
      withMissing: all.filter((r) =>
        TRACKED_KINDS.some((k) => (r.counts_by_kind[k] ?? 0) === 0),
      ).length,
      expiringSoon: all.filter((r) => r.next_expiry !== null).length,
    };
  }, [summary.data]);

  if (!canRead) {
    return (
      <Box>
        <PageHeader title={t("compliance.title")} subtitle={t("compliance.subtitle")} helpTerm="conformite" />
        <Alert severity="warning">{t("compliance.forbidden")}</Alert>
      </Box>
    );
  }

  const handleDownload = async (doc: PractitionerDocument) => {
    if (!drilldown) return;
    const { blob, filename } = await downloadPractitionerDocument(
      drilldown.practitioner_id,
      doc.id,
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <PageHeader
        title={t("compliance.title")}
        subtitle={t("compliance.subtitle")}
        actions={
          <>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<NotificationsActiveIcon />}
              onClick={() => expiryJob.mutate()}
              disabled={expiryJob.isPending}
            >
              {expiryJob.isPending
                ? t("compliance.runJobPending")
                : t("compliance.runJob")}
            </Button>
            <IconButton onClick={() => summary.refetch()} aria-label={t("common.retry")}>
              <RefreshIcon />
            </IconButton>
          </>
        }
      />

      {expiryJob.data && (
        <Alert
          severity={expiryJob.data.notified > 0 ? "success" : "info"}
          sx={{ mb: 2 }}
          onClose={() => expiryJob.reset()}
        >
          {t("compliance.runJobResult", {
            scanned: expiryJob.data.scanned,
            notified: expiryJob.data.notified,
            skipped: expiryJob.data.skipped_recently_notified,
          })}
        </Alert>
      )}
      {expiryJob.error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => expiryJob.reset()}>
          {String(
            (expiryJob.error as { response?: { data?: { detail?: string } } })
              .response?.data?.detail ?? "error",
          )}
        </Alert>
      )}

      {/* Quick stats */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 3, flexWrap: "wrap" }}>
        <StatCard
          icon={<CheckCircleIcon color="success" />}
          label={t("compliance.totalPractitioners")}
          value={stats.practitioners}
        />
        <StatCard
          icon={<ErrorIcon color="error" />}
          label={t("compliance.withExpired")}
          value={stats.withExpired}
        />
        <StatCard
          icon={<WarningIcon color="warning" />}
          label={t("compliance.withMissing")}
          value={stats.withMissing}
        />
        <StatCard
          icon={<WarningIcon color="info" />}
          label={t("compliance.expiringInHorizon", { days: horizonDays })}
          value={stats.expiringSoon}
        />
      </Stack>

      {/* Filters */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t("compliance.horizonLabel")}</InputLabel>
          <Select
            label={t("compliance.horizonLabel")}
            value={horizonDays}
            onChange={(e) => setHorizonDays(Number(e.target.value))}
          >
            {[30, 60, 90, 180, 365].map((d) => (
              <MenuItem key={d} value={d}>
                {d} {t("compliance.days")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t("compliance.filterLabel")}</InputLabel>
          <Select
            label={t("compliance.filterLabel")}
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
          >
            <MenuItem value="all">{t("compliance.filter.all")}</MenuItem>
            <MenuItem value="expired">{t("compliance.filter.expired")}</MenuItem>
            <MenuItem value="missing">{t("compliance.filter.missing")}</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          label={t("common.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
      </Stack>

      {/* Table */}
      {summary.isLoading ? (
        <CircularProgress />
      ) : rows.length === 0 ? (
        <Alert severity="info">{t("compliance.empty")}</Alert>
      ) : (
        <Paper variant="outlined" sx={{ overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("compliance.col.practitioner")}</TableCell>
                <TableCell>{t("compliance.col.rpps")}</TableCell>
                {TRACKED_KINDS.map((k) => (
                  <TableCell key={k} align="center" sx={{ minWidth: 60 }}>
                    {KIND_SHORT[k]}
                  </TableCell>
                ))}
                <TableCell align="center">{t("compliance.col.expired")}</TableCell>
                <TableCell>{t("compliance.col.nextExpiry")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.practitioner_id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setDrilldown(r)}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {r.last_name}
                      {r.first_initial && ` ${r.first_initial}.`}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>
                    {r.rpps ?? "—"}
                  </TableCell>
                  {TRACKED_KINDS.map((k) => {
                    const count = r.counts_by_kind[k] ?? 0;
                    return (
                      <TableCell key={k} align="center">
                        {count === 0 ? (
                          <Tooltip title={t("compliance.missing")}>
                            <Chip size="small" color="error" variant="outlined" label="✗" />
                          </Tooltip>
                        ) : (
                          <Chip size="small" color="success" label={count} />
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell align="center">
                    {r.expired_count > 0 ? (
                      <Chip size="small" color="error" label={r.expired_count} />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                    {r.next_expiry ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Drilldown drawer */}
      <Drawer
        anchor="right"
        open={drilldown !== null}
        onClose={() => setDrilldown(null)}
        slotProps={{ paper: { sx: { width: { xs: "100%", sm: 520 } } } }}
      >
        {drilldown && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {drilldown.last_name}
              {drilldown.first_initial && ` ${drilldown.first_initial}.`}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              RPPS: {drilldown.rpps ?? "—"}
            </Typography>

            {drilldownDocs.isLoading ? (
              <Box sx={{ mt: 3 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (drilldownDocs.data ?? []).length === 0 ? (
              <Alert severity="info" sx={{ mt: 3 }}>
                {t("compliance.noDocsForPractitioner")}
              </Alert>
            ) : (
              <Stack spacing={1} sx={{ mt: 3 }}>
                {(drilldownDocs.data ?? []).map((d) => (
                  <Paper key={d.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {d.label}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{ mt: 0.5, flexWrap: "wrap" }}
                        >
                          <Chip size="small" variant="outlined" label={t(`documents.kind.${d.kind}`)} />
                          {d.is_expired && (
                            <Chip size="small" color="error" label={t("documents.expired")} />
                          )}
                          {!d.is_expired &&
                            d.days_until_expiry !== null &&
                            d.days_until_expiry <= 60 && (
                              <Chip
                                size="small"
                                color="warning"
                                label={t("documents.expiringSoon", {
                                  days: d.days_until_expiry,
                                })}
                              />
                            )}
                          <Chip
                            size="small"
                            variant="outlined"
                            label={fmtSize(d.size_bytes)}
                            sx={{ fontFamily: "monospace" }}
                          />
                        </Stack>
                      </Box>
                      <Tooltip title={t("documents.download")}>
                        <IconButton size="small" onClick={() => handleDownload(d)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 200, flex: 1 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {icon}
        <Box>
          <Typography variant="caption" color="text.secondary" component="div">
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
